import {
  Alert,
  Button,
  Card,
  Group,
  NumberInput,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DataTable } from 'mantine-datatable';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { rawApi } from '../api/client';
import { listKey } from '../api/queryKeys';
import { type CashPair, MoneyPair, OverShortPair } from '../components/CashFigures';
import { useBusinessDate } from '../hooks/useSeedLocation';
import { formatDateTime, formatMoney, toIsoDate, toMinorUnits } from '../lib/format';
import { portalLocale } from '../lib/locale';
import { notifyDone, notifyError, notifySaved } from '../lib/notify';
import { businessDayCloseCopy } from './register/businessDayCloseCopy';
import { CashBoxLedger, type LedgerSubject } from './register/CashBoxLedger';
import { PullADrawer, useDrawers } from './register/PullADrawer';
import { Registers } from './register/RegistersAndDrawers';
import { registerPageCopy } from './register/registerPageCopy';
import { TheCashStory } from './register/TheCashStory';
import { TheNight } from './register/TheNight';
import { AWAITING_KEY, CountTheBox, TheOwnersChecks } from './register/TheOwnersChecks';
import { BoxStandingFloat, type FloatLine, SetWhatItHolds } from './register/TheStandingFloat';

/**
 * The register: what is in the drawers, what came up short, and where the cash went afterwards.
 *
 * Until now this screen did not exist in any form. The owner could set up a drawer and a float and
 * then had no way at all to see a count, a variance, a drop or a deposit — the drawer was a
 * conversation held at one in the morning and written on a piece of paper.
 *
 * Everything here is read-only except three things, and each is a fact only the office knows: what a
 * float is supposed to be, that money went to the bank, and that somebody checked the slip against
 * the statement. Counting and dropping happen at the register, where the cash physically is.
 */

interface CashBox {
  id: string;
  name: string;
  kind: 'drawer' | 'cajaita' | 'safe';
  standardFloatUsdMinor: number;
  standardFloatHnlMinor: number;
  active: boolean;
  /**
   * The standing float stated note by note, where it has been. Where lines exist the two totals
   * above are the server's own sums of them, and typing a total over them is refused.
   *
   * This used to say "empty on the drawers". It is not: the main drawer opens every day on the
   * owner's own composition, because a drawer holding the right value in the wrong notes cannot
   * make change, which is the entire job the float exists to do. Anything deciding which editor a
   * box gets reads THIS, not `kind`.
   */
  floatLines: FloatLine[];
}

interface SessionRow {
  id: string;
  registerName: string;
  ownerName: string | null;
  businessDayId: string;
  status: 'open' | 'closed';
  openedAt: string;
  closedAt: string | null;
  expected: CashPair;
  overShort: CashPair | null;
}

interface Balance {
  cashLocationId: string;
  name: string;
  kind: 'drawer' | 'cajaita' | 'safe';
  lastVerifiedCountAt: string | null;
  countedAtThen: CashPair;
  movedSince: CashPair;
  depositedSince: CashPair;
  /** Bills paid straight out of the box since, net of anything a reversal put back. */
  paidOutSince: CashPair;
  balance: CashPair;
}

interface Deposit {
  id: string;
  safeCashLocationId: string;
  amountUsdMinor: number;
  amountHnlMinor: number;
  reference: string | null;
  at: string;
  by: string;
  /** The name of whoever recorded it. The directory id beside it resolves to nobody here. */
  byName: string | null;
  verifiedBy: string | null;
  verifiedByName: string | null;
  verifiedAt: string | null;
}

/** What the boxes are called in English, so a stored word never reaches the screen as it is stored. */
const BOX_KIND: Record<CashBox['kind'], string> = {
  drawer: 'Cash drawer',
  cajaita: 'Float box',
  safe: 'Safe',
};

const localDate = (d: Date) => toIsoDate(d) ?? '';

/**
 * THE LAST SEVEN TRADING NIGHTS, anchored on the bar's day and not on the browser's.
 *
 * This screen's own heading says it — *"a night that ends at two in the morning belongs to the day
 * before it started"* — and its default range was built from `new Date()`, so the screen
 * contradicted its own sentence for six hours of every day and for anyone reading it from another
 * country. `/admin/v1/cash/sessions` ranges on `business_days.business_date`, which rolls at 04:00,
 * so between midnight and four — with the drawer that is being asked about still open — the browser
 * asked for a night the bar had not started.
 *
 * Noon, so shifting by whole days can never land on the neighbour.
 */
const lastSevenNights = (day: string): [string, string] => {
  const start = new Date(`${day}T12:00:00`);
  start.setDate(start.getDate() - 6);
  return [localDate(start), day];
};

function Section({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <Paper withBorder p="md" radius="md" mb="md">
      <Title order={5}>{title}</Title>
      {description && (
        <Text size="sm" c="dimmed" mt={2} mb="sm">
          {description}
        </Text>
      )}
      {children}
    </Paper>
  );
}

export function RegisterPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const closeLocale = portalLocale();
  const closeText = businessDayCloseCopy(closeLocale);
  const pageText = registerPageCopy(closeLocale);
  // Undefined until the location has loaded, and that is not the same as today: nothing is fetched
  // until the bar's own night is known, rather than fetching the reader's idea of it and correcting
  // the numbers afterwards.
  const businessToday = useBusinessDate();
  const [chosen, setChosen] = useState<[string, string] | null>(null);
  const [from, to] = chosen ?? (businessToday ? lastSevenNights(businessToday) : ['', '']);

  const sessions = useQuery({
    queryKey: ['register-sessions', from, to],
    queryFn: () =>
      rawApi<SessionRow[]>(
        'GET',
        `/admin/v1/cash/sessions?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
      ),
    enabled: Boolean(from && to),
  });

  const balances = useQuery({
    queryKey: listKey('/admin/v1/cash/balances'),
    queryFn: () => rawApi<Balance[]>('GET', '/admin/v1/cash/balances'),
  });

  const boxes = useQuery({
    queryKey: listKey('/admin/v1/cash/locations'),
    queryFn: () => rawApi<CashBox[]>('GET', '/admin/v1/cash/locations'),
  });

  const deposits = useQuery({
    queryKey: listKey('/admin/v1/cash/bank-deposits'),
    queryFn: () => rawApi<Deposit[]>('GET', '/admin/v1/cash/bank-deposits'),
  });

  /*
   * The float is saved from `SetWhatItHolds`, which PUTs the LINES and lets the server add them up.
   * The mutation that PUT the two totals from this page lived here and is gone with the editor that
   * called it: the server refuses those totals wherever lines exist, and a box with no lines is a
   * box with no standing float rather than one whose float is a pair of typed numbers.
   */

  const [deposit, setDeposit] = useState({
    boxId: '',
    usd: '' as number | '',
    hnl: '' as number | '',
    reference: '',
  });

  /**
   * CHECKING A DEPOSIT OFF AGAINST THE STATEMENT — which is a different thing from what this screen
   * used to claim, and the difference is the whole point.
   *
   * The sentence under this section promised *"whoever took it cannot be the one who confirms it
   * arrived"*. **Staff have no accounts here.** The owner records every deposit there is, so the one
   * person who could ever press this button was the one person the server turned away — the column
   * could only ever read "Not yet", under a screen saying the control was working.
   *
   * What it does now is a real check of a real fact by the only person who can perform it: the
   * statement is open, the line is on it, and his name and the time go on the row. It is not two
   * signatures and the page no longer says it is.
   */
  const confirmDeposit = useMutation({
    mutationFn: (id: string) => rawApi('POST', `/admin/v1/cash/bank-deposits/${id}/verify`, {}),
    onSuccess: () => {
      notifyDone(
        'Deposit checked off',
        'The slip is recorded as having arrived, with your name and the time.',
      );
      queryClient.invalidateQueries({ queryKey: listKey('/admin/v1/cash/bank-deposits') });
    },
    onError: (error) => notifyError(error, 'confirm this deposit'),
  });

  const recordDeposit = useMutation({
    mutationFn: () =>
      rawApi('POST', '/admin/v1/cash/bank-deposits', {
        safeCashLocationId: deposit.boxId,
        amountUsdMinor: toMinorUnits(deposit.usd) ?? 0,
        amountHnlMinor: toMinorUnits(deposit.hnl) ?? 0,
        reference: deposit.reference || null,
      }),
    onSuccess: () => {
      notifySaved('Deposit');
      setDeposit({ boxId: '', usd: '', hnl: '', reference: '' });
      queryClient.invalidateQueries({ queryKey: listKey('/admin/v1/cash/bank-deposits') });
      queryClient.invalidateQueries({ queryKey: listKey('/admin/v1/cash/balances') });
    },
    onError: (error) => notifyError(error, 'record the deposit'),
  });

  /*
   * Which cash box's movements are being read, or null.
   *
   * The balances on this page are DERIVED and never stored — the last count you checked, plus
   * everything that has moved since — which is the right way to hold them and leaves the owner with
   * a figure he cannot argue with. The ledger is the working behind it, and it is opened from the
   * figure rather than from a menu, because the moment anybody wants it is the moment the number
   * looks wrong.
   */
  const [ledgerBox, setLedgerBox] = useState<LedgerSubject | null>(null);

  /**
   * Which box is being counted, or null.
   *
   * Counting the safe was the one instrument this domain described in prose and could not perform.
   * Its balance is derived from the last count the owner checked; nothing could write one, so it
   * read as everything that had ever moved through the box and refused every deposit out of it.
   */
  const [countingBox, setCountingBox] = useState<{ id: string; name: string } | null>(null);

  /**
   * The box whose standing composition is being stated, or null.
   *
   * A float is a count sheet's worth of lines, and that is a modal's worth of screen rather than
   * something that fits in a table cell.
   */
  const [settingFloat, setSettingFloat] = useState<CashBox | null>(null);

  const held = (balances.data ?? []).filter((b) => b.kind !== 'drawer');
  /*
   * WHICH BOXES THE BANK TRIP CAN COME OUT OF, and it is not "everything that is not a register".
   *
   * This was `kind !== 'drawer'`, so the deposit form offered **From Cajaita** directly under its own
   * sentence saying that cash leaves *the safe* when it goes to the bank. Three things say the float
   * box is the wrong end of that trip and they all say it at once: the API's field is called
   * `safeCashLocationId`; `state.ts#boxMustNotGoNegative` deliberately exempts the float box, so a
   * deposit out of it is the one cash movement in this domain with no floor under it; and the card
   * six inches up this page says the float box's figure is "what has passed through it rather than
   * what is in it". Banking out of it would subtract a real trip to the bank from a figure the
   * screen has just told the owner does not mean what it looks like.
   *
   * The nightly profit is dropped into the safe and banked from the safe. That is the trip.
   */
  const safes = (boxes.data ?? []).filter((b) => b.kind === 'safe');

  /*
   * Which cash boxes have a shift open on them this minute. The same read the registers panel above
   * already makes, under the same cache key, so this costs nothing and cannot disagree with it.
   */
  const drawers = useDrawers();
  const tradingBoxIds = new Set(
    (drawers.data ?? []).filter((d) => d.tradingSessionId).map((d) => d.cashLocationId),
  );

  return (
    <Stack gap="md">
      <div>
        <Title order={3}>{pageText.title}</Title>
        <Text size="sm" c="dimmed">
          {pageText.description}
        </Text>
      </div>

      {/* WHAT IS WAITING FOR HIM, above everything, because it is the only part of this screen that
          asks him to do something. Every control it replaced demanded a second person: a second
          bartender to verify a closing count, a witness on the night drop, somebody other than the
          depositor to confirm the deposit. There is one bartender on a shift and one account on this
          site, so each of those refused every gesture it was asked about — under screens saying the
          control was working. This is the same question asked of the person who can answer it. */}
      <Section title={pageText.waitingTitle} description={pageText.waitingDescription}>
        <TheOwnersChecks locale={closeLocale} />
      </Section>

      {/* THE NIGHT ITSELF, above the money, because it is the thing all of the money belongs to —
          and because closing it is what stops these figures moving. Until this existed there was
          no moment at which last night's numbers were final: a night that had been counted,
          reported and handed to an accountant went on accepting sales. */}
      <Section title={closeText.sectionTitle} description={closeText.sectionDescription}>
        <TheNight locale={closeLocale} />
      </Section>

      {/*
        THE RULE, AND THE FIGURE THAT DISAGREES WITH IT.

        This heading used to say, flatly, that "the safe cannot be taken below nothing" — directly
        above a card reading Safe, minus two hundred and fifty dollars. Both halves were written the
        same week and neither was wrong: the floor is real and refuses a deposit out of the safe
        today, and the balance really is below nothing, because a deposit was recorded out of a safe
        that nothing had ever been recorded going INTO. The floor stops the next one; it cannot
        reach back and unmake the one that was already there, and no safe on any real site starts
        life at zero — the cash in it on the first morning arrived before this software existed.

        So a screen carrying the rule in the present tense over a figure that breaks it is the same
        defect as a Confirmed column with nothing to confirm, read the other way round: the owner
        believes something about his money that is not so. The rule is now stated as what it
        actually does — refuses the NEXT one — and a box already below nothing says so on its own
        card, with the thing to do about it, because that card is where somebody is standing when
        the deposit is refused.

        AND THEN THE SAME MISTAKE ONE SENTENCE LATER, found 2026-07-29 by reading this file's own
        comments against its own copy. The rewrite above says "**No box** will hand over more than it
        is recorded as holding" — and `cash/state.ts#boxMustNotGoNegative` returns true for a safe
        and false for everything else, which the comment eleven lines BELOW this one says out loud
        and at length. A float box is a box. So the heading promised a floor on the cajaita that
        nothing applies, over a card that is designed to go negative and explains why — which is the
        Confirmed-column defect a third time, in the paragraph written to describe it.

        It now names the two boxes the floor is actually asked of. The float box's exemption is left
        to its own card, because the reason is about that box and reads as an excuse anywhere else.
      */}
      <Section
        title="On hand"
        description="Worked out from the last count you checked, plus everything that has moved since, less what has gone to the bank. Nothing here is a stored figure. The safe will not hand over more than it is recorded as holding — a deposit or a transfer beyond that is refused, which is the rule the register has always applied to a drawer. The float box is not held to it: the change put into it by hand has nowhere to be recorded, so its figure is what has passed through it rather than what is in it."
      >
        <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }}>
          {held.map((box) => {
            /*
             * The standing float, off the same read the floats table below makes. Stated on the
             * card because the card is where the owner reads what the box holds, and "what it
             * holds" only means anything against "what it is supposed to hold".
             *
             * The top-up line is gated on a count having anchored the figure: until the box has
             * been counted once, the derived balance is "everything that has moved through it" —
             * the card says so two lines up — and a shortfall computed from that figure would be
             * an instruction to top up a box nobody has looked inside.
             */
            const row = (boxes.data ?? []).find((b) => b.id === box.cashLocationId);
            const standing =
              row && (row.standardFloatUsdMinor !== 0 || row.standardFloatHnlMinor !== 0)
                ? { usdMinor: row.standardFloatUsdMinor, hnlMinor: row.standardFloatHnlMinor }
                : null;
            const shortOf = standing
              ? {
                  usdMinor: standing.usdMinor - box.balance.usdMinor,
                  hnlMinor: standing.hnlMinor - box.balance.hnlMinor,
                }
              : null;
            const topUp = [
              shortOf && shortOf.usdMinor > 0 ? formatMoney(shortOf.usdMinor, 'USD') : null,
              shortOf && shortOf.hnlMinor > 0 ? formatMoney(shortOf.hnlMinor, 'HNL') : null,
            ].filter((part): part is string => part !== null);
            return (
              <Card
                key={box.cashLocationId}
                withBorder
                /*
                 * Opened from the figure, not from a menu, because the moment anybody wants the
                 * movements is the moment the figure looks wrong. A safe reading eight thousand
                 * lempiras short of what the owner expected is a night that was never dropped, a
                 * deposit recorded twice, or money gone — and the three are indistinguishable until
                 * somebody can see what actually moved.
                 */
                onClick={() => setLedgerBox({ id: box.cashLocationId, name: box.name })}
                style={{ cursor: 'pointer' }}
              >
                <Group justify="space-between" wrap="nowrap" align="flex-start">
                  <Text size="xs" c="dimmed">
                    {box.name}
                  </Text>
                  {/* The count that re-anchors this figure. On the card rather than in a menu, because
                    the moment anybody wants to count a box is the moment its number looks wrong. */}
                  <Button
                    size="compact-xs"
                    variant="subtle"
                    onClick={(e) => {
                      e.stopPropagation();
                      setCountingBox({ id: box.cashLocationId, name: box.name });
                    }}
                  >
                    Count it
                  </Button>
                </Group>
                <MoneyPair pair={box.balance} fw={700} />
                <Text size="xs" c="dimmed" mt={4}>
                  {box.lastVerifiedCountAt
                    ? `Last counted ${formatDateTime(box.lastVerifiedCountAt)}`
                    : 'Never counted — this is everything that has moved through it'}
                </Text>
                {standing && (
                  <Text size="xs" c="dimmed" mt={2}>
                    Should always hold {formatMoney(standing.usdMinor, 'USD')} ·{' '}
                    {formatMoney(standing.hnlMinor, 'HNL')}
                  </Text>
                )}
                {/* Not a shortage in the missing-money sense — the counts and their receipts answer
                  that. Bills have been paid out of it, so the change to make change with is gone,
                  and the thing to do is walk notes over from the safe and record the transfer. */}
                {box.lastVerifiedCountAt && topUp.length > 0 && (
                  <Text size="xs" c="orange.8" mt={2}>
                    {topUp.join(' and ')} short of what it should hold — top it up
                  </Text>
                )}
                {/*
                A box below nothing is not money missing, and a minus sign with no sentence beside
                it is exactly the kind of figure somebody acts on. Both kinds get a sentence now,
                and they are different sentences because the two causes are different and lead
                somewhere different:

                  - a FLOAT BOX is stocked by hand with change nobody records, so what is drawn here
                    is only what has passed through it. Nothing is owed and nothing is to be done —
                    which is why the floor is deliberately not applied to it (`cash/state.ts`).
                  - a SAFE is held to this figure. Below nothing means cash went out of it that
                    nothing was ever recorded putting in — the money that was in it on the first
                    morning, most likely. That is not an accusation, but it IS the reason the next
                    deposit out of it is refused, and saying so here is the difference between an
                    owner who records the drop and an owner who presses the button again.
              */}
                {(box.balance.usdMinor < 0 || box.balance.hnlMinor < 0) &&
                  (box.kind === 'safe' ? (
                    <Text size="xs" c="dimmed" mt={2} fs="italic">
                      Below nothing because more has gone out than was ever recorded going in — not money
                      missing. Nothing can be taken out of it until what put the cash there is recorded.
                    </Text>
                  ) : (
                    <Text size="xs" c="dimmed" mt={2} fs="italic">
                      Below nothing because the change put in by hand was never recorded — not money missing.
                    </Text>
                  ))}
              </Card>
            );
          })}
          {held.length === 0 && !balances.isLoading && (
            <Text size="sm" c="dimmed">
              No safe or float box has been set up yet.
            </Text>
          )}
        </SimpleGrid>
      </Section>

      {/*
        THE OTHER HALF OF "WHETHER WE ARE OPEN OR CLOSED", which had no screen and no door.

        A drawer on a shift is pulled from that shift's own page, below. A drawer with NO shift open
        on it was refused outright — "a cash drawer is counted through the shift it belongs to, not
        on its own" — which is a true sentence about a drawer with a shift and has nothing to say
        about a Sunday afternoon. It is also the state every register in this bar is in every hour it is
        closed, and the state every register in this system is in until a terminal exists to open one.

        It sits above the shifts because it is about the register as it stands now, and below the boxes
        because it is the same question asked of a different kind of box.

        And it lists EVERY register, not only the shut ones, which is what this sentence used to say —
        a drawer somebody is standing at is the first one he looks for, and a panel that quietly
        leaves it out reads as a register that has gone missing rather than one being served from.
      */}
      <Section
        title="What is in the registers"
        description="Every cash drawer, and what the last shift left in the ones nobody has a shift open on. Nothing can move a drawer between shifts, so that figure remains the same across business days — count one whenever you like, at any hour, and what you found goes on the record with your name and the time. A register that is trading is counted through its own shift, and the card says so."
      >
        <PullADrawer />
      </Section>

      <Section
        title="Drawers"
        description="Counted minus expected, per currency. Short means money is gone; over means a sale was rung wrong and somebody still has to explain it."
      >
        <Group mb="sm">
          <DatePickerInput
            type="range"
            size="xs"
            value={[from ? new Date(`${from}T12:00:00`) : null, to ? new Date(`${to}T12:00:00`) : null]}
            onChange={(value) => {
              const [start, end] = value as unknown as [Date | null, Date | null];
              if (start && end) setChosen([localDate(start), localDate(end)]);
            }}
          />
        </Group>
        <DataTable
          withTableBorder
          minHeight={120}
          fetching={sessions.isLoading}
          records={sessions.data ?? []}
          idAccessor="id"
          noRecordsText="No drawer was opened in these days."
          onRowClick={({ record }) => navigate(`/register/drawers/${record.id}`)}
          columns={[
            { accessor: 'registerName', title: 'Register' },
            { accessor: 'ownerName', title: 'Counted in by', render: (r) => r.ownerName ?? '—' },
            { accessor: 'openedAt', title: 'Opened', render: (r) => formatDateTime(r.openedAt) },
            {
              accessor: 'closedAt',
              title: 'Closed',
              render: (r) => (r.closedAt ? formatDateTime(r.closedAt) : 'Still open'),
            },
            {
              accessor: 'expected',
              title: 'Expected',
              render: (r) => <MoneyPair pair={r.expected} />,
            },
            {
              accessor: 'overShort',
              title: 'Over / short',
              render: (r) => <OverShortPair pair={r.overShort} />,
            },
          ]}
        />
      </Section>

      {/*
        THE STORY, under the drawers table because it answers over the same days the picker above
        chose — one range, one answer, and two sections that could never disagree about which days
        they are describing. It is a summary and not a second ledger: what each container was
        thought to hold, what was counted, the difference, and what moved, with the pouches that
        disagreed with their drawers called out by name. The ledger and the count sheets stay where
        they are for taking a wrong-looking figure apart.
      */}
      <Section
        title="The cash, told back"
        description="The same days as the drawers above, container by container: what we thought each was holding when it was last counted, what was actually counted, the difference, and what moved through it. Dollars and lempiras each on their own, never added together. A pouch that does not match the drawer it came from is called out — that disagreement is the number this page exists to surface."
      >
        <TheCashStory from={from} to={to} />
      </Section>

      {/*
        "CHANGING IT NEVER TOUCHES A DRAWER THAT IS ALREADY OPEN."

        That sentence was false for one figure out of three, and the way it was false landed on the
        bartender. The float counted in at the open is stored on the shift and a count keeps the
        expectation it was struck against — both were always frozen. THE DROP WAS NOT: what the bag
        owed was `(what is in the drawer + what has already gone) − this float`, read live off this
        row every time the panel was opened. Dropping the Main Drawer's float from L 2,000 to L 1,000
        turned "Above the float right now L 0.00" into L 500.00 without a note crossing the counter —
        and turned a shift counted off THE DAY BEFORE from L 221.00 owing into L 1,221.00 owing,
        which is `one-person-on-a-shift.md` property 2 broken: an expected figure recomputed against
        today's configuration is evidence of nothing.

        The repair went in the backend rather than in this copy. `cash/drop.ts` now reads the float
        the shift OPENED ON, off its own opening count, where `openSession` has been freezing it all
        along. So the original sentence is true now, and it is said plainly. The row still says when
        an edit lands, because "at the next open" is a useful thing to know and no longer a warning.
      */}
      <Section
        title="Standard floats"
        description="What a drawer is supposed to start the shift with, and what is put back into it before the rest goes in the bag. Changing it takes effect at the next open: a shift is measured against the float it started with, so nothing you do here moves a figure on a shift already running or on one already counted off. Most of these floats are stated note by note — how many of each bill the box is kept stocked with — because the point of a float is being able to make change, and the totals are worked out from the notes rather than typed."
      >
        <DataTable
          withTableBorder
          minHeight={100}
          fetching={boxes.isLoading}
          records={boxes.data ?? []}
          idAccessor="id"
          noRecordsText="No cash box has been set up yet."
          columns={[
            { accessor: 'name', title: 'Cash box' },
            { accessor: 'kind', title: 'Kind', render: (b) => BOX_KIND[b.kind] },
            {
              accessor: 'standardFloatUsdMinor',
              title: 'Standard float',
              /*
               * ONE EDITOR, BECAUSE A FLOAT IS A COMPOSITION WHEREVER IT IS ONE.
               *
               * This branched on `b.kind === 'drawer'` and gave a drawer two number boxes wired to a
               * PUT of the two totals. That was right while only the float box and the safe were
               * counted in notes. The moment the main drawer got the owner's own standing float —
               * $20×5, $10×5, $5×10, $1×20 and six lempira lines — it became a control that could
               * only fail: the server answers that PUT 409 `float_is_counted_in_notes` wherever
               * lines exist, and the refusal's own copy tells him to use "Set what it holds", which
               * this branch was the only thing that could have drawn and did not.
               *
               * The obvious repair — branch on whether lines exist — is wrong twice, and both are
               * the same bug wearing a different hat. The safe has no lines and no float, so it
               * would fall back to two number boxes and quietly acquire a float stated as a value.
               * And a drawer created tomorrow starts with no lines, so it would get the totals
               * editor and could never be given a composition at all: the door to the modal would
               * be the thing it needed lines to reach.
               *
               * So there is no predicate. Every box states its float note by note, which is what
               * the totals have been derived from since 0064, and a box with no float at all says
               * so and offers the same door. The two-totals editor is gone rather than left
               * unreachable — a screen with no way to type a total is what makes the server's
               * refusal something the owner can never meet.
               */
              render: (b) => (
                <BoxStandingFloat
                  box={b}
                  // Whether a bartender is standing at this drawer right now. Said on the row rather
                  // than in the heading, because the heading is read once and this is only true of
                  // one box on one evening.
                  tradingNow={tradingBoxIds.has(b.id)}
                  onEdit={() => setSettingFloat(b)}
                />
              ),
            },
            {
              // Here as well as on the cards above, because the cards deliberately show only the
              // safe and the float box — a cash drawer's own movements are reachable from nowhere
              // else, and a drawer that has had money moved in mid-shift is exactly the one whose
              // closing count somebody is about to argue about.
              accessor: 'id',
              title: '',
              textAlign: 'right',
              noWrap: true,
              render: (b) => (
                <Button size="compact-xs" variant="subtle" onClick={() => setLedgerBox(b)}>
                  Movements
                </Button>
              ),
            },
          ]}
        />
      </Section>

      {/* A register is not a cash box: the register is the point of sale a shift is opened against and
          a shortage is attributed through, and the box is the money. One register existed and it
          came out of a seed file, so the bar and the restaurant shared a drawer and no over or
          short could ever be put to a person. */}
      <Registers />

      <Section
        title="To the bank"
        description="Cash leaves the safe when it goes to the bank, so recording the trip is what brings the safe’s figure back down. Confirm one once the line is on your statement — that is a check of the money having arrived, and it is yours to make."
      >
        <Group align="flex-end" gap="sm" mb="sm" wrap="wrap">
          <TextInput
            label="Slip reference"
            size="xs"
            value={deposit.reference}
            onChange={(e) => setDeposit({ ...deposit, reference: e.currentTarget.value })}
          />
          <NumberInput
            label="Dollars"
            size="xs"
            prefix="$ "
            decimalScale={2}
            value={deposit.usd}
            onChange={(v) => setDeposit({ ...deposit, usd: v === '' ? '' : Number(v) })}
          />
          <NumberInput
            label="Lempiras"
            size="xs"
            prefix="L "
            decimalScale={2}
            value={deposit.hnl}
            onChange={(v) => setDeposit({ ...deposit, hnl: v === '' ? '' : Number(v) })}
          />
          <Button
            size="xs"
            disabled={!deposit.boxId || recordDeposit.isPending}
            onClick={() => recordDeposit.mutate()}
          >
            Record deposit
          </Button>
        </Group>
        <Group gap="xs" mb="sm">
          {safes.map((box) => (
            <Button
              key={box.id}
              size="xs"
              variant={deposit.boxId === box.id ? 'filled' : 'default'}
              onClick={() => setDeposit({ ...deposit, boxId: box.id })}
            >
              From {box.name}
            </Button>
          ))}
        </Group>
        {/* No safe set up is not the same as not having picked one, and the second sentence is the
            only one with something to do in it. */}
        {safes.length === 0 && !boxes.isLoading && (
          <Alert color="gray" mb="sm">
            No safe has been set up, so there is nothing for a deposit to come out of. Add one under the
            floats above and the takings can be dropped into it.
          </Alert>
        )}
        {safes.length > 0 && !deposit.boxId && (
          <Alert color="gray" mb="sm">
            Choose which safe the money came out of.
          </Alert>
        )}
        <DataTable
          withTableBorder
          minHeight={100}
          fetching={deposits.isLoading}
          records={deposits.data ?? []}
          idAccessor="id"
          noRecordsText="Nothing has been banked yet."
          columns={[
            { accessor: 'at', title: 'When', render: (d) => formatDateTime(d.at) },
            { accessor: 'reference', title: 'Slip', render: (d) => d.reference ?? '—' },
            {
              accessor: 'amountUsdMinor',
              title: 'Amount',
              render: (d) => <MoneyPair pair={{ usdMinor: d.amountUsdMinor, hnlMinor: d.amountHnlMinor }} />,
            },
            {
              accessor: 'byName',
              title: 'Taken by',
              render: (d) => d.byName ?? '—',
            },
            {
              accessor: 'verifiedBy',
              title: 'Checked off',
              /*
               * A name and a date, or the button that produces them. Never a bare "Yes": a
               * confirmation nobody can read is not worth having, and the id underneath resolves to
               * nobody — which is why the API records the name at the moment it is written.
               *
               * The button is offered on every unchecked row. It used to be withheld from whoever
               * recorded the deposit, which was everybody, so it was never offered at all.
               */
              render: (d) =>
                d.verifiedBy ? (
                  <div>
                    <Text size="sm">{d.verifiedByName ?? 'Checked'}</Text>
                    {d.verifiedAt && (
                      <Text size="xs" c="dimmed">
                        {formatDateTime(d.verifiedAt)}
                      </Text>
                    )}
                  </div>
                ) : (
                  <Button
                    size="compact-xs"
                    variant="light"
                    loading={confirmDeposit.isPending && confirmDeposit.variables === d.id}
                    onClick={() => confirmDeposit.mutate(d.id)}
                  >
                    It arrived
                  </Button>
                ),
            },
          ]}
        />
      </Section>

      {settingFloat && (
        <SetWhatItHolds
          box={settingFloat}
          onClose={() => setSettingFloat(null)}
          // The floats table and the balance cards both read the locations list, so one
          // invalidation brings the row's composition and the card's standing figure with it.
          onSaved={() => queryClient.invalidateQueries({ queryKey: listKey('/admin/v1/cash/locations') })}
        />
      )}

      <CashBoxLedger box={ledgerBox} onClose={() => setLedgerBox(null)} />
      <CountTheBox
        box={countingBox}
        locale={closeLocale}
        onClose={() => setCountingBox(null)}
        onCounted={() => {
          queryClient.invalidateQueries({ queryKey: listKey('/admin/v1/cash/balances') });
          queryClient.invalidateQueries({ queryKey: AWAITING_KEY });
        }}
      />
    </Stack>
  );
}
